// src/store/slices/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as authApi from '../api/auth';

// Async thunks
export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authApi.logout();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to logout');
    }
  }
);

const initialState = {
  user: JSON.parse(sessionStorage.getItem('user')) || null,
  token: sessionStorage.getItem('token') || null,
  refreshToken: sessionStorage.getItem('refreshToken') || null,
  isAuthenticated: !!sessionStorage.getItem('token'),
  loginStage: 'phone', // 'phone', 'otp'
  signupStage: 'form', // 'form', 'otp'
  verificationPhone: '',
  verificationType: '', // 'signup' or 'login'
  loading: false,
  error: null
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Set user data after successful login
    setUser: (state, action) => {
      state.user = action.payload;
      sessionStorage.setItem('user', JSON.stringify(action.payload));
    },
    
    // Set token after authentication
    setToken: (state, action) => {
      state.token = action.payload;
      state.isAuthenticated = !!action.payload;
      sessionStorage.setItem('token', action.payload);
    },
    
    // Set refresh token
    setRefreshToken: (state, action) => {
      state.refreshToken = action.payload;
      sessionStorage.setItem('refreshToken', action.payload);
    },
    
    // Start login flow
    startLoginFlow: (state, action) => {
      state.loginStage = 'phone';
      state.verificationPhone = '';
    },
    
    // Move to OTP verification stage during login
    setLoginOtpStage: (state, action) => {
      state.loginStage = 'otp';
      state.verificationPhone = action.payload;
      state.verificationType = 'login';
    },
    
    // Start signup flow
    startSignupFlow: (state) => {
      state.signupStage = 'form';
      state.verificationPhone = '';
    },
    
    // Move to OTP verification stage during signup
    setSignupOtpStage: (state, action) => {
      state.signupStage = 'otp';
      state.verificationPhone = action.payload;
      state.verificationType = 'signup';
    },
    
    // Reset verification stage
    resetVerificationStage: (state) => {
      state.loginStage = 'phone';
      state.signupStage = 'form';
      state.verificationPhone = '';
      state.verificationType = '';
    },
    
    // Logout user (client-side only)
    logout: (state) => {
      // Clear state
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.loginStage = 'phone';
      state.signupStage = 'form';
      
      // Clear session storage
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refreshToken');
    }
  },
  extraReducers: (builder) => {
    // Handle logoutUser async thunk
    builder
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, (state, action) => {
        // Clear state
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.loginStage = 'phone';
        state.signupStage = 'form';
        state.loading = false;
        
        // Clear session storage
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('refreshToken');
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to logout';
        
        // Even if API logout fails, clear session data
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        
        // Clear session storage
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('refreshToken');
      });
  }
});

export const { 
  setUser, 
  setToken, 
  setRefreshToken, 
  startLoginFlow,
  setLoginOtpStage,
  startSignupFlow,
  setSignupOtpStage,
  resetVerificationStage,
  logout 
} = authSlice.actions;

export default authSlice.reducer;